'use client';

import React from 'react';
import { useStore } from '@/store/store';
import { SvgLoader } from '@/app/elements/svg/loader';
import { TagSection } from './subcomponents/tag-section';
import { POPUPS } from '@/app/components/popup/constants';
import { type TagItem, getVolatilityConfig } from './utils/metadata-helpers';
import {
    isSwjJackpotTickerEnabled,
    SwjJackpotTicker
} from '@/app/components/site-wide-jackpot/subcomponents/jackpot-ticker';
import { useGlobalContext } from '@/store/contexts/global-context';

import './styles/default.scss';

type MetadataInfoSectionProps = {
    nativeDescription?: string | null;
    gameMode?: string | null;
    studioName?: string | null;
    isSWJTickerAvailable?: boolean;
    volatility?: string | null;
    themes?: TagItem[];
    features?: TagItem[];
    dataTestIdPrefix?: string;
};

export const MetadataInfoSection: React.FC<MetadataInfoSectionProps> = ({
    nativeDescription,
    gameMode,
    studioName,
    isSWJTickerAvailable = false,
    volatility,
    themes = [],
    features = [],
    dataTestIdPrefix = ''
}) => {
    const { queuePopup } = useStore((s) => s.popup);
    const { swjTickerRedesignVariant } = useGlobalContext();
    const volatilityConfig = getVolatilityConfig(volatility);

    const metaInfoText = [nativeDescription, gameMode, studioName].filter(Boolean).join(' | ');
    const hasMetaInfo = metaInfoText.length > 0;
    const hasVolatility = !!volatilityConfig;
    const showSWJTicker = isSWJTickerAvailable && isSwjJackpotTickerEnabled(swjTickerRedesignVariant);
    const hasThirdLine = showSWJTicker || hasVolatility;

    const handleVolatilityInfoClick = () => {
        queuePopup({ type: POPUPS.VOLATILITY_INFO });
    };

    return (
        <>
            {hasMetaInfo && (
                <div
                    className="gip-meta-info"
                    data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-meta-info` : undefined}>
                    {metaInfoText}
                </div>
            )}
            {hasThirdLine && (
                <div className="gip-third-line">
                    {showSWJTicker && (
                        <div
                            className="gip-swj-ticker-wrapper"
                            data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-swj-ticker` : undefined}>
                            <SwjJackpotTicker isSmall />
                        </div>
                    )}
                    {showSWJTicker && hasVolatility && (
                        <span
                            className="gip-meta-separator"
                            data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-meta-separator` : undefined}>
                            |
                        </span>
                    )}
                    {hasVolatility && (
                        <button
                            onClick={handleVolatilityInfoClick}
                            className="gip-volatility-info-btn"
                            aria-label="Learn more about volatility"
                            type="button">
                            <div
                                className="gip-volatility"
                                data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-volatility` : undefined}>
                                <volatilityConfig.icon className="gip-volatility-icon-img" />
                                <span className="gip-volatility-text">{volatilityConfig.display} volatility</span>
                                <SvgLoader iconName="volatility-info" className="gip-volatility-info-img" />
                            </div>
                        </button>
                    )}
                </div>
            )}
            <div className="gip-tags-scroll-container">
                <TagSection
                    title="Themes"
                    items={themes}
                    uidPrefix="theme-"
                    dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-themes` : undefined}
                />
                <TagSection
                    title="Features"
                    items={features}
                    uidPrefix="feature-"
                    dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-features` : undefined}
                />
            </div>
        </>
    );
};
